/**
 * StoryTooltip — shared pointer-following quote tooltip for the story lenses
 * (BraidLens now, MapLens in Task 9). Ported from the v6 mock's .maptip.
 *
 * The tip only mirrors information that is also available on click (the
 * drawer shows the same theme/date/quote), so it is aria-hidden.
 */
import { useState, type ReactNode } from "react";
import "./StoryTooltip.css";

export interface StoryTipContent {
  themeLabel: string;
  tone: string; // CSS color token, e.g. var(--color-danger)
  dateLabel: string;
  text: string;
}

interface PointerLike {
  clientX: number;
  clientY: number;
}

const TIP_MAX_W = 260;
const TIP_OFFSET = 14;
const TIP_MARGIN = 8;
const TIP_SNIPPET_LEN = 110;

export interface StoryTooltipApi {
  /** Render this once at the lens root. */
  tooltip: ReactNode;
  showTip: (content: StoryTipContent, e: PointerLike) => void;
  moveTip: (e: PointerLike) => void;
  hideTip: () => void;
}

export function useStoryTooltip(): StoryTooltipApi {
  const [content, setContent] = useState<StoryTipContent | null>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });

  const place = (e: PointerLike) => {
    // jsdom's fallback events carry no coordinates — treat as origin.
    const cx = Number.isFinite(e.clientX) ? e.clientX : 0;
    const cy = Number.isFinite(e.clientY) ? e.clientY : 0;
    const left = Math.max(
      TIP_MARGIN,
      Math.min(cx + TIP_OFFSET, window.innerWidth - TIP_MAX_W - TIP_MARGIN),
    );
    setPos({ left, top: cy + TIP_OFFSET });
  };

  const showTip = (c: StoryTipContent, e: PointerLike) => {
    setContent(c);
    place(e);
  };
  const moveTip = (e: PointerLike) => place(e);
  const hideTip = () => setContent(null);

  const tooltip: ReactNode = content ? (
    <div
      className="sty-tip"
      aria-hidden="true"
      style={{ left: pos.left, top: pos.top }}
    >
      <span className="sty-tip-theme" style={{ color: content.tone }}>
        {content.themeLabel}
      </span>
      <span className="sty-tip-date">· {content.dateLabel}</span>
      <div className="sty-tip-quote">
        „{content.text.slice(0, TIP_SNIPPET_LEN)}{content.text.length > TIP_SNIPPET_LEN ? "…" : ""}”
      </div>
    </div>
  ) : null;

  return { tooltip, showTip, moveTip, hideTip };
}
